import { useRouteMatch } from 'react-router';

import { UsersTab } from './UsersTab';
import { getUserName } from '../../utilities';
import { Breadcrumbs } from '../../components';
import { ConfirmModal } from '../../components';
import { AssignUserForm } from './AssignUserForm';
import { AssignLiveKitServerForm } from './AssignLiveKitServerForm';
import { useOrganization } from './useOrganization';
import { Button, Loading, Modal } from '../../components';
import { AssignUserRoleForm } from './AssignUserRoleForm';
import { UnassignUserRoleForm } from './UnassignUserRoleForm';
import { Copy, Status } from '../../components';
import { buildOrgManagementPath } from '../../app/Routes/orgRouteUtils';

type CounterProps = {
  label: string;
  value: React.ReactNode;
};
const Counter: React.FC<CounterProps> = ({ label, value }) => {
  return (
    <div className="flex flex-col gap-y-sm">
      <div className="text-mainText text-tiny font-medium">{label}</div>
      <div className="text-main text-section font-bold">{value}</div>
    </div>
  );
};

const Organization = () => {
  const { params } = useRouteMatch<{
    organization_id: string;
    id: string;
  }>();
  const {
    organization,
    users,
    loading,
    isOpened,

    onOpen,
    onClose,
    handleAddUser,
    userOptions,
    updating,

    rolesOptions,
    formForAction,

    onCloseRoleModal,
    isOpenedRoleModal,
    onOpenAddRoleModal,
    handleAssignUserRole,

    isOpenedUnassignModal,
    handleUnassignUser,
    onOpenUnassignModal,
    onCloseUnassignModal,

    isOpenedUnassignRoleModal,
    onOpenUnassignRoleModal,
    onCloseUnassignRoleModal,
    handleUnassignRoleUser,

    isOpenedApiKeyModal,
    onCloseApiKeyModal,
    apiKey,
    handleGetApiKey,
    setApiKey,

    isOpenedLiveKitModal,
    isLiveKitModalHidden,
    onOpenLiveKitModal,
    onCloseLiveKitModal,
    livekitServerOptions,
    currentLivekitServerId,
    pendingLivekitServerId,
    handleRequestAssignLiveKitServer,
    handleConfirmAssignLiveKitServer,
    handleCloseLiveKitConfirmModal,
    isOpenedLiveKitConfirmModal,
    assigningLiveKit,
  } = useOrganization(params.id);

  const handleCloseSecretModal = () => {
    onCloseApiKeyModal();
    setApiKey(null);
  };

  const findServerLabel = (id: string | null) =>
    livekitServerOptions.find(s => s.value === id)?.label ?? id;

  if (!organization || !params.id || loading) {
    return <>{loading && <Loading />}</>;
  }

  return (
    <>
      <div className="border-b pb-lg mb-lg">
        <Breadcrumbs
          className="mb-md text-small"
          subtitle="Organizations"
          subtitleUrl={buildOrgManagementPath(params.organization_id)}
          title={organization?.name ?? ''}
        />
        <div className="flex flex-row justify-between items-center mb-md">
          <div className="flex flex-row gap-x-md items-center">
            <span className="text-section font-bold text-mainText">
              {organization.name}
            </span>
            <Status active={organization.active} />
          </div>
          <div className="flex flex-row gap-x-lg items-center">
            <Counter label="Total Users" value={organization.userCount} />
            <Counter label="Active Users" value={organization.userCount} />
          </div>
        </div>
        <div className="flex flex-row gap-x-md items-center">
          <Button
            className="h-fit"
            type="outline"
            onClick={handleGetApiKey}
            label="Show API key"
          />
          <Button
            className="h-fit"
            onClick={onOpenLiveKitModal}
            label="Assign LiveKit Server"
          />
          <Button
            className="h-fit"
            onClick={onOpen}
            label="Assign user to Organization"
          />
        </div>
      </div>
      <ConfirmModal
        open={isOpenedUnassignModal}
        onClose={onCloseUnassignModal}
        onConfirm={handleUnassignUser}
        key={String(isOpenedUnassignModal) + '_unassign_user'}
        title={`Remove ${getUserName(formForAction)} from organization?`}
      />
      <Modal
        size="small"
        title="Assign user"
        open={isOpened}
        onClose={onClose}
        key={String(isOpened) + '_assign_user'}
      >
        <AssignUserForm
          saving={updating}
          users={userOptions}
          onCancel={onClose}
          onSave={handleAddUser}
        />
      </Modal>
      <Modal
        size="small"
        title="Assign role"
        open={isOpenedRoleModal}
        onClose={onCloseRoleModal}
        key={String(isOpenedRoleModal) + '_assign_role'}
      >
        <AssignUserRoleForm
          saving={updating}
          roles={rolesOptions}
          user={formForAction}
          onCancel={onCloseRoleModal}
          onSave={handleAssignUserRole}
        />
      </Modal>
      <Modal
        size="small"
        title="Remove user role"
        open={isOpenedUnassignRoleModal}
        onClose={onCloseUnassignRoleModal}
        key={String(isOpenedUnassignRoleModal) + '_unassign_role'}
      >
        <UnassignUserRoleForm
          saving={updating}
          roles={rolesOptions}
          user={formForAction}
          onCancel={onCloseUnassignRoleModal}
          onSave={form => handleUnassignRoleUser(form.roleId)}
        />
      </Modal>
      <Modal
        size="small"
        open={isOpenedApiKeyModal}
        onClose={handleCloseSecretModal}
        title="API key"
      >
        <div className="flex flex-col">
          <div className="flex flex-row gap-x-md justify-end">
            <Copy
              label="Copy key"
              className="text-buttonPrimary text-tiny"
              text={apiKey || ''}
            />
          </div>
          <div className="flex flex-col gap-y-md justify-center items-center my-md">
            <p className="text-center text-xl">{apiKey}</p>
          </div>
        </div>
        <div className="flex flex-row justify-center">
          <Button
            size="medium"
            type="contrast"
            label="Close"
            onClick={handleCloseSecretModal}
          />
        </div>
      </Modal>

      <Modal
        size="small"
        title="Assign LiveKit Server"
        open={isOpenedLiveKitModal}
        onClose={onCloseLiveKitModal}
        visuallyHidden={isLiveKitModalHidden}
        key={String(isOpenedLiveKitModal) + '_assign_livekit'}
      >
        <AssignLiveKitServerForm
          saving={assigningLiveKit}
          servers={livekitServerOptions}
          currentServerId={currentLivekitServerId}
          onCancel={onCloseLiveKitModal}
          onSave={handleRequestAssignLiveKitServer}
        />
      </Modal>
      <ConfirmModal
        open={isOpenedLiveKitConfirmModal}
        saving={assigningLiveKit}
        onClose={handleCloseLiveKitConfirmModal}
        onConfirm={handleConfirmAssignLiveKitServer}
        key={String(isOpenedLiveKitConfirmModal) + '_confirm_livekit'}
        title=""
        confirmButtonLabel="Confirm"
        confirmButtonClassName="w-full bg-red-500 border-red-500 hover:bg-red-600 hover:border-red-600"
      >
        <p className="flex flex-wrap justify-center gap-x-1 text-mainText text-small">
          <span>Are you sure you want to move</span>
          <span className="font-medium">{organization.name}</span>
          <span>from</span>
          <span className="font-medium">{findServerLabel(currentLivekitServerId)}</span>
          <span>to</span>
          <span className="font-medium">{findServerLabel(pendingLivekitServerId)}?</span>
        </p>
      </ConfirmModal>

      <UsersTab
        users={users}
        loading={loading}
        updating={updating}
        onOpenAddRoleModal={onOpenAddRoleModal}
        onOpenUnassignModal={onOpenUnassignModal}
        onOpenRemoveRoleModal={onOpenUnassignRoleModal}
      />
    </>
  );
};

export default Organization;
